import React, { useEffect, useState } from 'react';
import { ANY, DEFAULT_FORM_ROW_GUTTER, EFFECTIVE, LABEL } from '../../utils/Constants';
import {
  checkIsSecondaryItem,
  getRandomKey,
  parseJSONValueFromAutocomplete,
  prepareSuggestionsWithCustomLabels,
  transformAutocompleteAgency,
  transformCarriersAutocompleteData,
} from '../../utils/MiscUtils';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import FormItemLabel from 'antd/lib/form/FormItemLabel';
import Checkbox from '../../components/Checkbox/Checkbox';
import Autosuggest from '../../components/Autosuggest/Autosuggest';
import { fetchAgencies, fetchAirlines, fetchStationLocations, fetchWrapper, SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM } from '../../utils/ApiUtils';
import { IAutoCompleteChangeHandlerProps } from '../../views/SearchesManager/SearchManagerForm/SearchManagerForm';
import { IAutocompleteObject } from '../../store/state/SearchManager/FormData';
import SMFormSelect from '../../views/SearchesManager/SearchManagerForm/SMFormSelect/SMFormSelect';
import Select from 'antd/lib/select';
import FormInput from '../../components/FormInput/FormInput';
import { transformAutocompleteData } from '../../utils/MiscUtils';
import { IItem, IRow } from '../EditorLayout/EditorLayout';
import { Configuration } from './FiltersTableLayoutsWrapper';
import { FiltersData } from './FiltersTableLayoutsWrapperV2';
import Collapse from 'antd/lib/collapse';
import { BrandAndMarketListItem } from '../../integrated-client';
import TableFormatter from '../../utils/TableFormatter';
import Button from 'antd/lib/button/button';
import styles from './FiltersLayoutV2.module.scss';

const { Panel } = Collapse;

interface IFiltersLayoutV2Props {
  handleFiltersDataUpdate: (value: any) => void; // to be replaced to Utils
  filtersData: FiltersData;
  configuration: Configuration | null;
  clearValues: Array<keyof FiltersData> | null;
  activeKeys: string | Array<string>;
  setActiveKeys: (keys: Array<string> | string) => void;
  setClearValues: (value: Array<keyof FiltersData> | null) => void;
  resetClearValues: () => void;
  rows: Array<IRow>;
  title?: string;
  collapsible?: boolean;
  handleClearButtonClick: () => void;
  handleSearchButtonClick: () => void;
  handleExportButtonClick?: () => void;
}

interface IHandleBlur {
  id: keyof FiltersData;
  value: string | boolean | number | IAutocompleteObject | null;
}

interface IDetail {
  value: any;
  text: string;
}

const FiltersLayoutV2 = (props: IFiltersLayoutV2Props) => {
  const {
    handleFiltersDataUpdate,
    filtersData,
    configuration,
    clearValues,
    activeKeys,
    setActiveKeys,
    setClearValues,
    resetClearValues,
    rows,
    title,
    collapsible = true,
    handleClearButtonClick,
    handleSearchButtonClick,
    handleExportButtonClick,
  } = props;

  // Return null if filtersData is not yet initialized
  if (!filtersData) {
    return null;
  }

  const handleAutoSuggestBlur = ({ id, value }: IHandleBlur) => {
    const storeValue: any = id ? filtersData[id] : null;
    if (storeValue === null) {
      setClearValues([id]);
      return true;
    }
    //to be move on function in miscUtils (`(${storeValue?.code}) ${storeValue?.label}`)
    if (getDisplayText({ id } as IItem, storeValue) !== value) {
      handleFiltersDataUpdate({ [id]: null });
      setClearValues([id]);
      return true;
    }
    return false;
  };

  const handleAutocompleteChange = (props: IAutoCompleteChangeHandlerProps) => {
    const { value, option, formField } = props;
    if (option?.value) {
      let parsedVal = parseJSONValueFromAutocomplete(option.value);
      const newValObj: any = {
        [formField]: {
          label: parsedVal.label,
          code: parsedVal.code || parsedVal.value,
          isLocation: parsedVal.isLocation === true || parsedVal.isLocation === false ? parsedVal.isLocation : undefined,
          effective: parsedVal.effective === true || parsedVal.effective === false ? parsedVal.effective : undefined,
        },
      };
      if (formField === 'agency') {
        newValObj[formField].id = parsedVal.id;
        newValObj[formField].code = parsedVal.code;
      }
      if (formField === 'carrier') {
        newValObj[formField].id = parsedVal.id;
      }
      handleFiltersDataUpdate(newValObj);
    } else if (value === undefined && option === undefined && formField) {
      handleFiltersDataUpdate({ [formField]: null });
    }
    return value;
  };

  const handleSelect = (value: string, _: any, fieldName: string) => {
    let newVal;
    try {
      newVal = JSON.parse(value);
    } catch {
      newVal = null;
    }
    handleFiltersDataUpdate({ [fieldName]: newVal });
  };

  const isEffective = (value: any) => (value.hasOwnProperty('effective') && !value.effective ? 'nonEffectiveDetail' : undefined);

  const hasCheckbox = (item: IItem) => item.checkbox && filtersData[item.checkbox.id];

  const getDisplayText = (item: IItem, value: any) => {
    if (!item || !value) return null;
    let displayText;
    displayTextRenderMethods.forEach((arrayOfId: Array<string>, renderFunc: any) => {
      if (arrayOfId.includes(item.id)) displayText = renderFunc(value, item);
    });
    return displayText || renderDefaultDisplayText(value);
  };

  const getDetailText = (item: IItem, value: any) => `${getDisplayText(item, value)}${hasCheckbox(item) ? `(${item.checkbox?.label})` : ''}`;

  const renderDefaultDisplayText = (value: any) => (value.label ? value.label : value);
  const renderWithCodeDisplayText = (value: any) => `(${value.code}) ${value.label}`;
  const renderBrandAndMarketDisplayText = (value: BrandAndMarketListItem) => (value ? TableFormatter.formatBrandAndMarketObject(value) : null);
  const renderCheckboxDisplayText = (value: any, item: IItem) => {
    return value ? item.label : null;
  };

  const displayTextRenderMethods = new Map([
    [renderWithCodeDisplayText, ['agency', 'origin', 'destination']],
    [renderDefaultDisplayText, ['default']],
    [renderBrandAndMarketDisplayText, ['brandAndMarket']],
    [renderCheckboxDisplayText as any, ['showDeleted', 'showDisabled']],
  ]);

  const renderFiltersTitle = (title: string | undefined) => {
    let details: Array<IDetail> = [];
    !activeKeys.length &&
      rows.forEach((row: IRow) => {
        row.items.forEach((item) => {
          let detail;
          const value = filtersData[item.id];
          if (value) {
            detail = { value, text: getDetailText(item, value) };
            details.push(detail);
          } else if (item.innerItems) {
            item.innerItems.forEach((innerItem: IItem) => {
              const value = filtersData[innerItem.id];
              if (value) {
                detail = { value, text: getDetailText(innerItem, value) };
                details.push(detail);
              }
            });
          }
        });
      });

    return (
      <div className="filtersTitle">
        {title}
        {details.length ? (
          <div className={'detailsContainer'}>
            (
            {details.map((detail: any, i: number) => (
              <span className={isEffective(detail.value)}>
                {detail.text}
                {!(i === details.length - 1) && ', '}
              </span>
            ))}
            )
          </div>
        ) : null}
      </div>
    );
  };

  const renderInput = (item: IItem) => {
    const { id, label, inputType = 'text', span = '6', allowClear = false, isTrimRequired = false, onPressEnter } = item;

    return (
      <Col span={span} id={`input-${id}`}>
        <div className="item">
          <FormInput
            id={id}
            labelText={label}
            allowClear={allowClear && !!filtersData[id]}
            type={inputType}
            value={filtersData[id] || ''}
            onChange={(e: any) => handleFiltersDataUpdate({ [id]: e.target.value || null })}
            onBlur={(e: any) => isTrimRequired && handleFiltersDataUpdate({ [id]: e.target.value.trim() || null })}
            onPressEnter={onPressEnter}
          />
        </div>
      </Col>
    );
  };

  const renderSelect = (item: IItem) => {
    const { id, label, span = '6', allowClear = false, configSource, configurationProperty, transform, clearValues, hideAnyOption = false } = item;
    const newConfiguration = configSource && configurationProperty ? configSource : configuration;
    if (clearValues && clearValues?.length > 0) {
      setClearValues([...clearValues]);
    }
    return (
      <Col span={span} id={`select-${id}`}>
        <div className="item">
          <SMFormSelect
            id={id}
            wrapperClassName={styles.filtersItem}
            labelText={label}
            allowClear={allowClear && !!filtersData[id]}
            // value={(filtersData[id] as any)?.label || ANY}           //to be typed
            value={filtersData[item.id] ? getDisplayText(item, filtersData[item.id]) : ANY} //to be typed
            hasSecondaryValueSelected={(filtersData[id] as any)?.effective === false} //to be typed
            onChange={(value: string, option: any) => handleSelect(value, option, id || [])}
            options={() => (
              <>
                {!hideAnyOption && <Select.Option value={ANY}>{ANY}</Select.Option>}
                {configurationProperty &&
                  newConfiguration &&
                  (newConfiguration as any)[configurationProperty]?.map((elem: any) => {
                    const el = transform ? transform(elem) : elem;
                    if (el?.effective === false) return null;
                    return (
                      <Select.Option key={`${el?.id ?? getRandomKey()}`} value={JSON.stringify(el)}>
                        {getDisplayText(item, el)}
                      </Select.Option>
                    );
                  })}
              </>
            )}
          />
        </div>
      </Col>
    );
  };

  const renderAutoSuggest = (item: IItem) => {
    const { id, label, span = '6', allowClear = false, checkbox, disabled } = item;

    return (
      <>
        <Col span={span} id={`autosuggest-${id}`}>
          <div className="item">
            <FormItem>
              <FormItemLabel prefixCls={id} htmlFor={id} label={label} />
              <Autosuggest
                startValue={getDisplayText(item, filtersData[id]) || null}
                id={id}
                hasSecondaryValueSelected={checkIsSecondaryItem(filtersData[id], EFFECTIVE)}
                clearValue={clearValues?.includes(id)}
                clearedValueCallback={resetClearValues}
                allowClear={allowClear && !!filtersData[id]}
                useCustomOptions={true}
                useCustomOptionsNewDesign={true}
                customOptionLabelField={LABEL}
                fetchFunction={fetchWrapper((fetchObject[id] as any).fetchFunc, (fetchObject[id] as any).params)}
                onBlurHandler={handleAutoSuggestBlur}
                resultDataTransformer={(fetchObject[id] as any)?.transformDataFunc}
                renderOptionsHandler={prepareSuggestionsWithCustomLabels(EFFECTIVE)}
                changeHandler={(value: string, option: any) =>
                  handleAutocompleteChange({
                    value,
                    option,
                    formField: id,
                  })
                }
                disabled={disabled}
              />
            </FormItem>
          </div>
        </Col>
        {checkbox && renderCheckbox(checkbox)}
      </>
    );
  };

  const renderCheckbox = (item: IItem) => {
    const { id, label, span = '6' } = item;

    return (
      <Col span={span} id={`checkbox-${id}`}>
        <div className="checkbox-container">
          <Checkbox id={id} checked={filtersData[id]} onChange={() => handleFiltersDataUpdate({ [id]: !filtersData[id] })} />
          <FormItemLabel prefixCls={id} htmlFor={id} label={label} />
        </div>
      </Col>
    );
  };

  const renderContainer = (item: IItem) => {
    const { label, span = '6', innerItems } = item;

    return (
      <Col span={span} className={`item-wrapper-${item.id}`}>
        <div className="item">
          <div className={`container container-${item.id}`}>
            {label && <span className="container-label">{label} </span>}
            {innerItems?.map((item: IItem) => (elements as any)[item.type](item))}
          </div>
        </div>
      </Col>
    );
  };

  const renderButton = (item: IItem) => {
    const { buttonType, label, span = '6' } = item;

    return (
      <Col span={span} className={styles.btn} id={`button-${buttonType}`}>
        {(buttons as any)[buttonType as any](label)}
      </Col>
    );
  };

  const renderClear = (label: string) => (
    <Button block type="link" onClick={handleClearButtonClick}>
      {label}
    </Button>
  );

  const renderExport = (label: string) => (
    <Button block type="link" onClick={handleExportButtonClick}>
      {label}
    </Button>
  );

  const renderSearch = (label: string) => (
    <Button block type="primary" onClick={handleSearchButtonClick}>
      {label}
    </Button>
  );

  const fetchObject = {
    agency: {
      fetchFunc: fetchAgencies,
      transformDataFunc: transformAutocompleteAgency,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
        ...((filtersData && 'brandAndMarket' in filtersData && filtersData.brandAndMarket)
          ? {
            brandsAndMarkets: [filtersData.brandAndMarket],
            strictBrandAndMarket: true
          }
          : {
            brandsAndMarkets:
              (configuration as any)?.brandsAndMarkets && (configuration as any)?.brandsAndMarkets.length
                ? [...(configuration as any)?.brandsAndMarkets]
                : undefined,
          }),
      },
    },
    carrier: {
      fetchFunc: fetchAirlines,
      transformDataFunc: transformCarriersAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
    oneAirLocation: {
      fetchFunc: fetchStationLocations,
      transformDataFunc: transformAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
    origin: {
      fetchFunc: fetchStationLocations,
      transformDataFunc: transformAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
    destination: {
      fetchFunc: fetchStationLocations,
      transformDataFunc: transformAutocompleteData,
      params: {
        ...SHOW_NON_EFFECTIVE_TRUE_EXTRA_PARAM,
      },
    },
  };

  type renderButtonFunc = (label: string) => JSX.Element;

  interface IButtons {
    clear: renderButtonFunc;
    search: renderButtonFunc;
    export: renderButtonFunc;
  }

  const buttons: IButtons = {
    clear: renderClear,
    search: renderSearch,
    export: renderExport,
  };

  type renderFunc = (item: IItem) => JSX.Element;

  interface IElements {
    select: renderFunc;
    autoSuggest: renderFunc;
    input: renderFunc;
    checkbox: renderFunc;
    container: renderFunc;
    button: renderFunc;
  }

  const elements: IElements = {
    select: renderSelect,
    autoSuggest: renderAutoSuggest,
    input: renderInput,
    checkbox: renderCheckbox,
    container: renderContainer,
    button: renderButton,
  };

  return (
    <>
      <Form>
        <Collapse
          bordered={false}
          activeKey={collapsible ? activeKeys : ['1']}
          onChange={(keys: Array<string> | string) => setActiveKeys(keys)}
          className={styles.customFiltersCollapse}
        >
          <Panel
            header={renderFiltersTitle(title)}
            className={styles.customFiltersPanel}
            key="1"
            collapsible={collapsible ? undefined : 'disabled'}
            showArrow={collapsible}
          >
            {rows && rows.map((row: IRow) => <Row gutter={DEFAULT_FORM_ROW_GUTTER}>{row.items.map((item: IItem) => (elements as any)[item.type](item))}</Row>)}
          </Panel>
        </Collapse>
      </Form>
    </>
  );
};

export default FiltersLayoutV2;
